<template>
  <div id="pu-table-container">
    <div class="header">
      Dashboard Users
    </div>
    <div class="sub-header">
      Create new users, manage your existing list, and set role permissions here.
    </div>
    <div v-if="show" class="explanation-container">
      <div class="flex-row just-between">
        <div class="sac__font-18 n50">
          <b>
            Role permissions
          </b>
        </div>
        <button
          type="button"
          @click="toggleShow(false)"
        >
          <v-icon color="black" medium>
            mdi-close
          </v-icon>
        </button>
      </div>
      <div class="sac__font-14 n50 mt-3">
        <b>Admin</b>: Full access. Edit customer information,
        create new users, change platform settings
      </div>
      <div class="sac__font-14 n50 mt-3">
        <b>Manager</b>: Edit customer information. Cannot create new users.
      </div>
      <div class="sac__font-14 n50 mt-3">
        <b>Viewer</b>: Read-only access (no editing permission)
      </div>
    </div>
    <div class="pu-header-container">
      <div class="flex-row align-center">
        <div class="flex-1 flex-row align-center">
          <SACSelect
            v-if="branches.length > 2"
            :value="selectedCorp"
            :options="branches"
            :change="changeBranch"
            containerClass="branch-dropdown-container"
          />
          <SACCheckbox
            v-if="filteredPlatformUsers.length > 0"
            label="Show Only Active Users"
            :checked="showOnlyActive"
            :change="() => showOnlyActive = !showOnlyActive"
          />
        </div>
      </div>
      <div class="flex-row">
        <button
          v-if="adminRole === 'admin'"
          type="button"
          class="create-btn"
          @click="toggleActionModal(true, 'new-user', {})"
        >
          <v-icon color="white" medium>
            mdi-account-plus-outline
          </v-icon>
          Create New User
        </button>
        <div class="search-container">
          <input
            aria-label="Search"
            placeholder="Search"
            style="width: 200px;"
            type="search"
            :value="puTableSearch"
            @input="changeSettingValue('puTableSearch', $event.target.value)"
          />
          <img :class="['search-icon']" src="@shared/assets/img/table/search.png" alt="search">
        </div>
      </div>
    </div>
    <div class="put-container">
      <v-data-table
        :headers="tableHeaders"
        :items="filteredPlatformUsers"
        :loading="loadingPlatformUsers"
        hide-default-header
        :options="puTableOptions"
        :search="puTableSearch"
        :custom-filter="customFilter"
        fixed-header
        hide-default-footer
        :custom-sort="customSort"
      >
        <template #header="{ props }">
          <th
            v-for="head, idx in props.headers"
            :key="idx"
            class="text-left sac__font-16 pl-4 semi-bold"
          >
            {{ head.text }}
            <button v-if="head.text === 'Role'" type="button" class="icon-Circle-Question ml-1" @click="showRoleInfo" />
          </th>
        </template>
        <template #[`item.name`]="{ item }">
          <td>
            {{
              item.firstName && item.lastName
                ? `${item.firstName} ${item.lastName}`
                : '--'
            }}
          </td>
        </template>
        <template #[`item.creationDate`]="{ item }">
          <td>
            {{ date(item.creationDate) }}
          </td>
        </template>
        <template v-if="adminRole === 'admin'" #[`item.edit`]="{ item }">
          <td>
            <button
              type="button"
              class="edit-btn"
              @click="toggleActionModal(true, 'edit-user', item);"
            >
              Edit
            </button>
          </td>
        </template>
      </v-data-table>
    </div>
    <SACTableFooter
      :totalRecords="filteredPlatformUsers.length"
      :rowCount="puTableOptions.itemsPerPage"
      :changeRowCount="(count) => changeTableOptions('puTableOptions', 'itemsPerPage', count)"
      :page="puTableOptions.page"
      :totalPage="platformUsersRecordCount"
      :changePage="(page) => changeTableOptions('puTableOptions', 'page', page)"
      :loading="loadingPlatformUsers"
    />
  </div>
</template>

<script>
import SACSelect from '@shared/components/sac/SACSelect.vue';
import SACCheckbox from '@shared/components/sac/SACCheckbox.vue';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import apiClient from '@shared/lib/general/apiClient';
import SACTableFooter from '../table/SACTableFooter.vue';
import settingMixin from '../../mixins/settingMixin';

export default {
  name: 'PlatformUserTable',
  components: {
    SACTableFooter,
    SACSelect,
    SACCheckbox,
  },
  mixins: [settingMixin, unitPreferencesMixin],
  props: {
    currentCorporationP: Object,
    toggleModal: Function,
  },
  data() {
    return {
      branches: [],
      show: true,
      showOnlyActive: true,
    };
  },
  computed: {
    tableHeaders() {
      let headers = [{
        text: 'Name',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'name',
      }, {
        text: 'Email',
        sortable: true,
        filterable: true,
        value: 'email',
      }];
      // if parent corp
      if (this.currentAdmin.parentCorp || this.isSmacAdmin) {
        headers = [
          ...headers,
          {
            text: 'Company Name',
            sortable: true,
            filterable: true,
            value: 'companyName',
          },
        ];
      }
      headers = [
        ...headers,
        {
          text: 'Role',
          sortable: true,
          filterable: true,
          value: 'role',
        }, {
          text: 'Creation Date',
          sortable: true,
          filterable: true,
          value: 'creationDate',
        }, {
          text: 'Account Status',
          sortable: true,
          filterable: true,
          value: 'accountStatus',
        }, {
          text: '',
          sortable: false,
          filterable: false,
          value: 'edit',
        },
      ];
      return headers;
    },
    filteredPlatformUsers() {
      if (this.showOnlyActive) {
        return this.platformUsers.filter((u) => u.accountStatus?.toLowerCase() === 'active');
      }
      return this.platformUsers;
    },
    platformUsersRecordCount() {
      const {
        filteredPlatformUsers,
        puTableOptions: {
          itemsPerPage,
        },
      } = this;
      if (filteredPlatformUsers.length === 0) {
        return 0;
      }
      return Math.ceil(filteredPlatformUsers.length / itemsPerPage);
    },
  },
  async mounted() {
    this.$store.dispatch('getAllPlatformUsers', this.currentCorporationP.id);

    let branches = [];
    const { data: pBranches } = await apiClient({
      method: 'GET',
      url: `/corporations/id/${this.currentCorporationP.id}/branches`,
    });
    const sorted = pBranches.sort((a, b) => a.corporationName.localeCompare(b.corporationName));
    branches = [{
      id: 0,
      corporationName: 'Branches - All',
    },
    ...sorted,
    ];
    this.changeBranch(0);
    this.branches = branches.map((el) => ({ value: el.id, label: el.corporationName }));
  },
  methods: {
    customSort(items, index, isDesc) {
      this.changeTableOptions('puTableOptions', 'sortBy', index);
      this.changeTableOptions('puTableOptions', 'sortDesc', isDesc);

      items.sort((a, b) => {
        if (index[0] === 'companyName') {
          if (!a.companyName) {
            return 1;
          }
          if (!b.companyName) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.companyName.toLowerCase() < b.companyName.toLowerCase() ? -1 : 1;
          }
          return b.companyName.toLowerCase() < a.companyName.toLowerCase() ? -1 : 1;
        }
        if (index[0] === 'name') {
          if (!a.firstName && !a.lastName) {
            return 1;
          }
          if (!b.firstName && !a.lastName) {
            return -1;
          }
          const nameA = `${a.firstName.toLowerCase()} ${a.lastName.toLowerCase()}`;
          const nameB = `${b.firstName.toLowerCase()} ${b.lastName.toLowerCase()}`;
          if (!isDesc[0]) {
            return nameA < nameB ? -1 : 1;
          }
          return nameB < nameA ? -1 : 1;
        }
        if (index[0] === 'email') {
          if (!a.email) {
            return 1;
          }
          if (!b.email) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.email.toLowerCase() < b.emailtoLowerCase() ? -1 : 1;
          }
          return b.emailtoLowerCase() < a.emailtoLowerCase() ? -1 : 1;
        }
        if (index[0] === 'role') {
          if (!a.role) {
            return 1;
          }
          if (!b.role) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.role < b.role ? -1 : 1;
          }
          return b.role < a.role ? -1 : 1;
        }
        if (index[0] === 'creationDate') {
          if (!a.creationDate) {
            return 1;
          }
          if (!b.creationDate) {
            return -1;
          }
          const dateA = new Date(a.creationDate);
          const dateB = new Date(b.creationDate);

          if (!isDesc[0]) {
            return dateA < dateB ? -1 : 1;
          }
          return dateB < dateA ? -1 : 1;
        }
        return 0;
      });

      return items;
    },
    showRoleInfo() {
      this.toggleModal(true, 'role-info');
    },
    customFilter(value, search, item) {
      const s = search?.toLowerCase();
      const name = `${item?.corporationName}`?.toLowerCase();
      const fullName = `${item.firstName} ${item.lastName}`?.toLowerCase();
      const role = `${item?.role}`?.toLowerCase();
      const email = `${item?.email}`?.toLowerCase();
      const searchDate = this.date(item.creationDate)?.toLowerCase();
      const accountStatus = `${item?.accountStatus}`?.toLowerCase();

      return (
        value != null
        && s != null
        && typeof value === 'string'
        && (value.toString().toLowerCase().indexOf(s) !== -1
          || (fullName && fullName.indexOf(s) !== -1)
          || (name && name.indexOf(s) !== -1)
          || (role && role.indexOf(s) !== -1)
          || (searchDate && searchDate.indexOf(s) !== -1)
          || (name && name.indexOf(s) !== -1)
          || (email && email.indexOf(s) !== -1)
          || (accountStatus && accountStatus.indexOf(s) !== -1)
        )
      );
    },
    toggleShow(val) {
      this.show = val;
    },
    changeBranch(v) {
      const corpId = Number(v);
      this.changeSettingValue('selectedCorp', corpId);
    },
  },
};
</script>

<style lang="scss">
#pu-table-container {
  width: 100%;
  padding: 20px;

  //max-width: 1705px;
  .pu-header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;

    .create-btn {
      padding: 13px 18px;
      border-radius: 4px;
      background-color: $NBLUE100;
      color: $WHITE;
      font-weight: 600;
      margin-right: 24px;

      &:hover {
        background-color: $NBLUE90;
      }
    }
  }

  .explanation-container {
    background-color: #F6F6F7;
    border-radius: 8px;
    padding: 12px 20px;
    margin-bottom: 20px;

    b {
      font-weight: 500;
    }
  }

  .header {
    font-size: 20px;
    font-weight: 600;
    color: black;
  }

  .sub-header {
    font-size: 18px;
    margin-bottom: 20px;
    font-weight: 400;
    color: $N60;
  }

  .sac-checkbox {
    border: 2px solid $N40;

    &.active {
      border: 0px white;
    }
  }

  .search-container {
    background-color: #F6F6F7;
    border-radius: 4px;
    padding: 12px 18px;
    align-items: center;
    display: flex;

    .search-icon {
      height: 18px;
    }
  }

  .branch-dropdown-container {
    .sac-select {
      background-color: #F6F6F7;
      border-radius: 4px;
      height: fit-content;
      padding: 13px 18px;
    }

    margin-right: 12px;
    width: 200px;
  }

  .put-container {
    background-color: $WHITE;
    padding-bottom: 12px;

    .v-data-table__wrapper {

      thead {
        box-shadow: none;

        tr {
          th {
            color: black;
            background-color: white;
            box-shadow: none;
            font-size: 15px;
            font-weight: 500;
            text-transform: uppercase;

            .mdi-checkbox-blank-outline::before,
            .mdi-checkbox-marked::before,
            .mdi-minus-box::before,
            .mdi-arrow-up::before {
              color: $WHITE;
            }

            &:first-child {
              border-top-left-radius: 6px;
            }

            &:last-child {
              border-top-right-radius: 6px;
            }

            &.sortable {
              i {
                display: none;
              }
            }

            &.active {
              i {
                display: inline-block;
              }
            }
          }
        }
      }

      tbody {
        tr {
          td {
            border-bottom: 0;

            .mdi-checkbox-marked::before {
              color: $NBLUE100;
            }

            .edit-btn {
              color: $NBLUE110;
              border-bottom: 2px solid $NBLUE110;
              font-size: 14px;
              padding: 3px;
            }
          }

          &:hover {
            background: none;
          }

          &:nth-child(even) {
            background-color: $NBLUE5;
          }
        }
      }
    }
  }
}
</style>
